import { Link, Navigate, useParams } from 'react-router-dom';
import './ProductModulePage.css';

const modulePageDetails = {
  resident: {
    slug: 'resident-experience',
    name: 'Resident Experience',
    seoTitle: 'Resident Experience & Visitor Management Software | Garde',
    metaDescription: 'Give residents one app for visitor approvals, service requests, payments, estate notices, permissions and real-time updates.',
    audience: 'Residents, tenants and household members',
    outcome: 'Fewer calls to the estate office and a clearer experience for every household.',
    workflow: [
      { title: 'Billing', description: 'Receive unit-level rent, service charge, utility and other property invoices.' },
      { title: 'Payments', description: 'Pay through supported channels and see receipts and balances against the correct account.' },
      { title: 'Service requests', description: 'Report an issue, follow its status and receive the completion update.' },
      { title: 'Notices', description: 'Receive official estate announcements, reminders and shared documents in one place.' },
    ],
  },
  estate: {
    slug: 'estate-administration',
    name: 'Estate Administration',
    seoTitle: 'Estate Management Software in Kenya | Garde',
    metaDescription: 'Manage units, occupants, landlords, incidents, communications and audit trails in one estate administration system built for Kenya.',
    audience: 'Estate managers, administrators and committees',
    outcome: 'One dependable record for every unit, occupant and estate activity.',
  },
  billing: {
    slug: 'billing-engine',
    name: 'Billing Engine',
    seoTitle: 'Property Billing, Rent & Service Charge Software | Garde',
    metaDescription: 'Run rent, service charge and utility billing with invoices, receipts, payment reconciliation, reminders and property-level reporting.',
    audience: 'Estate accountants, property managers and finance teams',
    outcome: 'Accurate charges, visible collections and fewer manual reconciliations.',
  },
  security: {
    slug: 'security-access',
    name: 'Security & Access',
    seoTitle: 'Visitor & Estate Security Management Software | Garde',
    metaDescription: 'Manage visitor verification, vehicle access, exit passes, guard shifts, incident logs and emergency alerts from one security record.',
    audience: 'Security teams, supervisors and estate managers',
    outcome: 'Faster entry decisions with a complete, traceable security record.',
    workflow: [
      { title: 'Visitor approval', description: 'Residents pre-authorise guests or guards request confirmation against the host and unit.' },
      { title: 'Entry and exit', description: 'Verify identity, record the visit and retain arrival and departure times.' },
      { title: 'Exit permissions', description: 'Check approved movement of household assets, vehicles, children or service providers.' },
      { title: 'Incidents', description: 'Log threats, breaches and operational incidents with the responsible team and follow-up action.' },
      { title: 'Guard handover', description: 'Transfer open incidents, instructions and pending actions between shifts.' },
    ],
  },
  work: {
    slug: 'work-orders',
    name: 'Work Orders',
    seoTitle: 'Property Maintenance & Work Order Software | Garde',
    metaDescription: 'Create, assign, schedule and track property maintenance work, workers, vendors, costs and completion evidence in one system.',
    audience: 'Maintenance teams, supervisors and service providers',
    outcome: 'Every request has an owner, a deadline, a cost and a clear completion record.',
    workflow: [
      { title: 'Request', description: 'A resident, inspection or incident identifies the work required and the affected unit or asset.' },
      { title: 'Work order', description: 'Management defines the scope, priority, assignee, due date and required evidence.' },
      { title: 'Procurement', description: 'Materials or external services move through an approved purchase linked back to the job.' },
      { title: 'Completion', description: 'The team records labour, materials, notes, photos, approval and the final cost before closing.' },
    ],
  },
  landlord: {
    slug: 'landlord-module',
    name: 'Landlord Module',
    seoTitle: 'Landlord & Property Portfolio Management Software | Garde',
    metaDescription: 'Give landlords a consolidated view of properties, occupancy, statements, settlements, leases, maintenance and documents.',
    audience: 'Landlords, property owners and authorised representatives',
    outcome: 'Owners stay informed without chasing managers for updates and statements.',
    workflow: [
      { title: 'Collections', description: 'See rent and other owner-related amounts collected against the correct units.' },
      { title: 'Recoveries', description: 'Review approved property costs and recoverable amounts attributed to the landlord.' },
      { title: 'Statements', description: 'Receive a clear opening balance, collections, deductions, recoveries and closing position.' },
      { title: 'Settlement', description: 'Confirm the net amount due to the owner and retain the remittance and payment record.' },
    ],
  },
  agent: {
    slug: 'my-agent',
    name: 'My Agent',
    seoTitle: 'Managing Agent & Multi-property Management Software | Garde',
    metaDescription: 'Manage client portfolios, agreements, management fees, remittances, income, expenses and profitability across multiple properties.',
    audience: 'Managing agents and property management companies',
    outcome: 'A clear separation between client property operations and the management business.',
  },
  documents: {
    slug: 'documents',
    name: 'Documents',
    seoTitle: 'Property Document Management Software | Garde',
    metaDescription: 'Organise leases, ownership records, vendor files and compliance documents with permissions, approvals, expiry alerts and version history.',
    audience: 'Property teams, owners, auditors and authorised stakeholders',
    outcome: 'The latest document stays connected to the property record it supports.',
  },
  accounting: {
    slug: 'ledgers-accounting',
    name: 'Ledgers & Accounting',
    seoTitle: 'Property Accounting & Estate Ledgers Software | Garde',
    metaDescription: 'Manage estate transactions, receivables, payables, funds, budgets, reconciliation and financial reporting from connected property ledgers.',
    audience: 'Property accountants, estate managers and finance committees',
    outcome: 'A complete financial picture for each estate, fund and reporting period.',
    workflow: [
      { title: 'Accounts receivable', description: 'Billing posts resident and tenant balances into the receivables subledger.' },
      { title: 'Accounts payable', description: 'Approved supplier invoices create obligations in the payables subledger.' },
      { title: 'Journals', description: 'Source transactions and authorised adjustments post balanced debits and credits.' },
      { title: 'Subledgers', description: 'Customer, supplier, landlord and fund detail remains traceable to control accounts.' },
      { title: 'Reconciliation', description: 'Bank, billing, AR and AP balances are compared and exceptions resolved.' },
      { title: 'Statements', description: 'Produce operational statements and financial reports from the reconciled ledger.' },
    ],
  },
  procurement: {
    slug: 'procurement',
    name: 'Procurement',
    seoTitle: 'Property Procurement & Purchase Order Software | Garde',
    metaDescription: 'Control property purchasing from requests and quotations through approvals, purchase orders, supplier invoices and reporting.',
    audience: 'Estate managers, procurement teams and approvers',
    outcome: 'Every purchase is linked to its approval, supplier, budget and operational purpose.',
    workflow: [
      { title: 'Purchase request', description: 'State what is required, why it is needed and which estate, work order or budget owns the cost.' },
      { title: 'RFQ', description: 'Invite comparable supplier quotations and retain the commercial evaluation.' },
      { title: 'Purchase order', description: 'Convert the approved choice into a controlled order with quantities, price and delivery terms.' },
      { title: 'Receipt', description: 'Record goods or services received, including partial delivery and rejected quantities.' },
      { title: 'GRNI', description: 'Recognise received value before the supplier invoice arrives and track the unmatched receipt.' },
      { title: 'Supplier invoice', description: 'Match the invoice to the order and receipt, then resolve quantity or price exceptions.' },
      { title: 'Accounts payable', description: 'Create the verified supplier obligation against the right estate, fund and accounts.' },
      { title: 'Payment', description: 'Approve and record settlement, clear the payable and retain the full source trail.' },
    ],
  },
  payroll: {
    slug: 'payroll',
    name: 'Payroll',
    seoTitle: 'Estate Staff & Property Payroll Software | Garde',
    metaDescription: 'Manage permanent and casual estate staff, compensation, attendance inputs, payroll runs, payslips and payroll reporting.',
    audience: 'Estate administrators, HR teams and payroll approvers',
    outcome: 'Controlled payroll runs with clear employee records and traceable costs.',
  },
  assets: {
    slug: 'asset-maintenance',
    name: 'Asset Maintenance',
    seoTitle: 'Property Asset & Preventive Maintenance Software | Garde',
    metaDescription: 'Track property assets, preventive maintenance, inspections, warranties, service contracts, work orders and lifecycle costs.',
    audience: 'Facilities teams, technicians and estate managers',
    outcome: 'Less reactive maintenance and a complete service history for every asset.',
  },
  marketplace: {
    slug: 'marketplace',
    name: 'Garde Market',
    seoTitle: 'Verified Property Marketplace in Kenya | Garde Market',
    metaDescription: 'Discover verified property listings, keep listing conversations together and manage rental or purchase applications against real availability.',
    audience: 'Property seekers, landlords, agents and developers',
    outcome: 'Fewer stale listings and a clearer path from discovery to application and onboarding.',
    workflow: [
      { title: 'Verified listing', description: 'An authorised owner, agent or developer publishes a real unit with current availability.' },
      { title: 'Discovery', description: 'Prospects compare relevant property details and understand the estate behind the listing.' },
      { title: 'Conversation', description: 'Questions and follow-ups stay connected to the selected property and responsible lister.' },
      { title: 'Application', description: 'Rental applications or purchase interest are submitted against the correct listing.' },
      { title: 'Onboarding', description: 'Approved applicants continue with the verified property and stakeholder context intact.' },
    ],
  },
};

export const modulePages = Object.entries(modulePageDetails).map(([key, details]) => ({ key, ...details }));

export function getModulePageByPath(pathname) {
  const slug = pathname.match(/^\/modules\/([^/]+)\/?$/)?.[1];
  return modulePages.find((page) => page.slug === slug);
}

function SiteHeader() {
  return (
    <header className="product-header">
      <div className="product-shell product-header-inner">
        <Link className="product-brand" to="/" aria-label="Garde home">
          <img src="/assets/garde-logo.jpg" alt="" />
          <span>Garde<span aria-hidden="true">.</span></span>
        </Link>
        <nav aria-label="Product page navigation">
          <Link to="/#platform">Platform</Link>
          <Link to="/#modules">Modules</Link>
          <Link to="/#pricing">Pricing</Link>
        </nav>
        <a className="product-header-cta" href="https://app.garde.co.ke">Property Management</a>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="product-footer">
      <div className="product-shell product-footer-inner">
        <p>© {new Date().getFullYear()} Garde by Probt Solutions Ltd.</p>
        <div>
          <Link to="/privacy-policy">Privacy policy</Link>
          <Link to="/terms-of-service">Terms of service</Link>
          <Link to="/data-deletion">Data deletion</Link>
        </div>
      </div>
    </footer>
  );
}

export default function ProductModulePage({ modules }) {
  const { slug } = useParams();
  const page = modulePages.find((item) => item.slug === slug);
  if (!page || !modules[page.key]) return <Navigate to="/" replace />;

  const module = modules[page.key];
  const related = modulePages.filter((item) => item.key !== page.key).slice(0, 4);
  const workflow = page.workflow || module.features.slice(0, 3);
  const productUrl = page.key === 'marketplace' ? 'https://market.garde.co.ke' : 'https://app.garde.co.ke';

  return (
    <div className="product-page">
      <SiteHeader />
      <main>
        <section className="product-hero">
          <div className="product-shell product-hero-grid">
            <div className="product-hero-copy">
              <nav className="product-breadcrumb" aria-label="Breadcrumb">
                <Link to="/">Garde</Link><span>/</span><Link to="/#modules">Modules</Link><span>/</span><span>{page.name}</span>
              </nav>
              <span className="product-kicker">{module.kicker}</span>
              <h1>{module.title}</h1>
              <p>{module.description}</p>
              <div className="product-actions">
                <Link className="product-button product-button-primary" to="/#contact-form">Book a demo</Link>
                <a className="product-button product-button-secondary" href={productUrl}>Open {page.key === 'marketplace' ? 'Garde Market' : 'Garde'} <span aria-hidden="true">↗</span></a>
              </div>
              <dl className="product-result">
                <div><dt>Built for</dt><dd>{page.audience}</dd></div>
                <div><dt>Operational result</dt><dd>{page.outcome}</dd></div>
              </dl>
            </div>
            <div className="product-hero-visual">
              <img src="/assets/garde-app-mockup.jpg" alt="Garde property management dashboards on mobile devices" />
            </div>
          </div>
        </section>

        <section className="product-capabilities">
          <div className="product-shell">
            <div className="product-section-heading">
              <span className="product-kicker">What the module covers</span>
              <h2>Everything needed to run this part of the property.</h2>
              <p>Each capability works from the same estate, unit and stakeholder records used across Garde.</p>
            </div>
            <div className="product-capability-grid">
              {module.features.map((feature, index) => (
                <article key={feature.title} className="product-capability-card">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="product-flow">
          <div className="product-shell product-flow-grid">
            <div className="product-section-heading">
              <span className="product-kicker">End-to-end product depth</span>
              <h2>How {page.name} works in Garde.</h2>
              <p>The public view shows the operational chain behind the module, including the records, controls and hand-offs that connect each stage.</p>
            </div>
            <ol className="product-flow-list">
              {workflow.map((feature, index) => (
                <li key={feature.title}>
                  <span>{index + 1}</span>
                  <div><h3>{feature.title}</h3><p>{feature.description}</p></div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="product-related">
          <div className="product-shell">
            <div className="product-related-head">
              <div><span className="product-kicker">Explore Garde</span><h2>Connected modules</h2></div>
              <Link to="/#modules">View all modules</Link>
            </div>
            <div className="product-related-grid">
              {related.map((item) => (
                <Link key={item.slug} to={`/modules/${item.slug}`}>
                  <span>{item.name}</span><span aria-hidden="true">↗</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="product-cta">
          <div className="product-shell product-cta-card">
            <div><span className="product-kicker">See it with your property</span><h2>Bring {page.name.toLowerCase()} into one operating system.</h2></div>
            <Link className="product-button product-button-light" to="/#contact-form">Book a Garde demo</Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
